import { createQueryClient } from '@realtyfeed/data-access';
import { QueryClientProvider } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { Toaster } from 'react-hot-toast';

/**
 * Everything the tree needs above the router.
 *
 * The query client is created in state rather than at module scope so a test can mount the
 * providers twice without the second mount inheriting the first one's cache — which is how a
 * suite ends up with tests that pass alone and fail together.
 *
 * No `PortalContainerProvider`: Radix portals to `document.body`, the `ds-*` tokens are
 * declared on `:root`, and a body-mounted dialog is therefore already themed. That provider is
 * for the narrow case of portalled content that has to live inside a particular subtree — see
 * its own note in `@realtyfeed/portal`.
 */
export const AppProviders = ({ children }: { children: ReactNode }): ReactNode => {
  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <Toaster position="top-right" toastOptions={{ duration: 4000 }} />
    </QueryClientProvider>
  );
};
